import { Icon, Input, InputProps } from '@billy/design-system'
import { ChangeEvent, FocusEvent, ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { Company, FrenchCompany } from '@modules-deprecated/app/companies/types'

// The @components barrel re-exports CreateContactModal, which renders this field through the
// create-supplier modal's company registry search, so importing the barrel here cycles back.
// Moving the supplier modal out of views/ does not fix this; it can import from @components again once the barrel
// stops re-exporting CreateContactModal and ContactSelect (EXP-660).
// eslint-disable-next-line @billy/shine-accounting/consistent-alias-imports -- importing the barrel from here cycles back through CreateContactModal
import { CompanySelect } from '../../../../components/selects/CompanySelect/CompanySelect'
import { Scope } from '../../../../enums/scope'
import { useCompanySearchField } from './hooks/useCompanySearchField'
import * as Styled from './styles'

type CompanySearchFieldProps = InputProps & {
  onChange: (name: string) => void
  onSelectCompany: (company: Partial<Company> | Partial<FrenchCompany>) => void
  value?: string
}

export const CompanySearchField = ({
  autoFocus = true,
  onChange,
  onSelectCompany,
  size = 'sm',
  value,
  ...restOfProps
}: CompanySearchFieldProps): ReactElement => {
  const { t } = useTranslation()
  const {
    createFreeTextEntry: handleFreeTextCreate,
    isLoadingDetailedCompany,
    isCompanySearchActive,
    selectCompany: handleCompanySelect,
    selectedCompany,
    updateCompanyName,
  } = useCompanySearchField({
    onChange,
    onSelectCompany,
  })

  const shouldAutoFocus = autoFocus && !value

  const handleFreeTextChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => updateCompanyName(e.currentTarget.value),
    [updateCompanyName],
  )

  const handleInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      updateCompanyName(e.currentTarget.value)
    },
    [updateCompanyName],
  )

  if (isCompanySearchActive) {
    return (
      <CompanySelect
        {...restOfProps}
        allowCreate
        autoFocus={shouldAutoFocus}
        inputValue={value}
        onBlur={handleInputBlur}
        onCreate={handleFreeTextCreate}
        onSelect={handleCompanySelect}
        placeholder={t('companies.company_search_field.placeholder')}
        readOnly={isLoadingDetailedCompany}
        scopes={Scope.ContactRegisterLookup}
        selectedId={selectedCompany?.id || undefined}
        selectedValue={(selectedCompany as Partial<Company>) || undefined}
        size={size}
        suffix={
          isLoadingDetailedCompany ? (
            <Styled.RotatingIconWrapper>
              <Icon icon="circleDashed" />
            </Styled.RotatingIconWrapper>
          ) : undefined
        }
      />
    )
  }

  return (
    <Input {...restOfProps} autoFocus={shouldAutoFocus} onChange={handleFreeTextChange} size={size} value={value} />
  )
}
